import { getLocale } from "@nerves/i18n/runtime";
import { ArrowDown, ArrowElbowDownLeft, ArrowLeft, ArrowRight, ArrowUp, type Icon } from "@phosphor-icons/react";
import { cx } from "@/lib/cx";

export const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Platform label for the primary modifier (Cmd on Mac, Ctrl or Strg elsewhere). */
export const modLabel = () => (isMac ? "⌘" : getLocale() === "de" ? "Strg" : "Ctrl");

// Arrow and return glyphs are drawn as icons: Geist Mono has no arrows, and the system fallback on
// Windows renders them as colour emoji.
const icons: Record<string, Icon> = {
  enter: ArrowElbowDownLeft,
  up: ArrowUp,
  down: ArrowDown,
  left: ArrowLeft,
  right: ArrowRight,
};

/**
 * Keyboard hint. `keys` are shown as separate caps; "mod" becomes Cmd on macOS and Ctrl elsewhere,
 * and "enter", "up", "down", "left", "right" become glyphs. Purely visual: the real handler lives in
 * lib/shortcuts. Deliberately quieter than the text it annotates (10.5px mono, hairline cap).
 *
 * `tone="inverse"` is for hints on a primary button or inside a tooltip.
 */
export function Kbd({ keys, className, tone }: { keys: string[]; className?: string; tone?: "inverse" }) {
  return (
    <span className={cx("inline-flex shrink-0 items-center gap-0.5", className)}>
      {keys.map((k, i) => {
        const lower = k.toLowerCase();
        const I = icons[lower];
        return (
          <kbd
            // biome-ignore lint/suspicious/noArrayIndexKey: static short list
            key={i}
            className={cx(
              "inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-mark px-1 font-mono text-[10.5px] leading-none font-medium tnum",
              tone === "inverse"
                ? "bg-primary-fg/15 text-primary-fg"
                : "border border-kbd-border bg-kbd-bg text-kbd-fg",
            )}
          >
            {I ? <I size={10} weight="bold" aria-label={lower} /> : lower === "mod" ? modLabel() : k}
          </kbd>
        );
      })}
    </span>
  );
}
